import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '../utils/toast';
import { Loader2, Plus, X, Building2 } from 'lucide-react';
import axios from 'axios';
import { sortStringOptions, getCRMInstitutionTypes } from '@/utils/sortOptions';
import { DISPOSITION_STATUSES } from '@/utils/dispositionStatuses';
import { getApiErrorMessage } from '@/utils/apiError';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddLeadPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { leadId } = useParams(); // Get leadId from URL params for edit mode
  const isEditMode = !!leadId; // Determine if in edit mode
  const [loading, setLoading] = useState(false);
  const [loadingLead, setLoadingLead] = useState(isEditMode); // Loading existing lead
  const [crmOfficers, setCrmOfficers] = useState([]);
  const [loadingOfficers, setLoadingOfficers] = useState(false);
  const [isConverted, setIsConverted] = useState(false); // Track if lead is converted to hospital
  const [convertedHospitalId, setConvertedHospitalId] = useState(null);

  // Rich text editor config for Important Notes (basic formatting per requirement)
  const notesModules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['clean']
    ]
  }), []);
  const notesFormats = ['bold', 'italic', 'list', 'bullet'];
  
  // Institution name suggestions state
  const [institutionSuggestions, setInstitutionSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const institutionInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Duplicate institution detection: null | { exists, is_own, owner_name, lead_id }
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [duplicateDismissed, setDuplicateDismissed] = useState(false);
  // Creator shown under the page title ("Added by")
  const [addedByName, setAddedByName] = useState('');
  
  // Get demo data from navigation state
  const demoData = location.state?.demoData || null;
  // Safely get user role
  const getUserRole = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.role || '';
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
    return '';
  };
  
  const [userRole] = useState(getUserRole());
  
  // Initialize SPOC list with demo contact person if available
  const [spocList, setSpocList] = useState([{
    spoc_name: demoData?.contact_person_name || '',
    spoc_mobile: demoData?.contact_phone || '',
    spoc_email: demoData?.contact_email || '',
    spoc_designation: '',
    remarks: ''
  }]);
  
  const [formData, setFormData] = useState({
    institution_type: '',
    hospital_name: demoData?.hospital_name || '',
    hospital_address: '',
    country: 'India',
    state: '',
    city: '',
    pin_code: '',
    contact_email: demoData?.contact_email || '',
    contact_phone: demoData?.contact_phone || '',
    number_of_beds: demoData?.number_of_beds || '',
    number_of_staff: demoData?.number_of_staff || '',
    number_of_concurrent_staff: 0,
    number_of_staff_for_quotation: 0,
    gstin_uin: '',
    pan_it: '',
    accreditation_status: '',
    disposition_status: '',
    follow_up_action: '',
    important_notes: '',
    lead_source: '',
    quoted_amount: 0,
    assigned_officer_email: '' // For Super Admin to assign lead to CRM Officer
  });

  // Use centralized institution types for consistency across CRM and Signup
  const institutionTypes = getCRMInstitutionTypes();

  const bedOptions = ['Up to 50', '100', '200', '300', '500', '1000', '2000', 'Above 2000'];
  const staffOptions = ['Up to 50', '100', '200', '300', '500', '1000', '2000', 'Above 2000'];
  
  const spocDesignations = sortStringOptions([
    'Hospital Administrator',
    'Quality Assurance Team',
    'HR Manager',
    'Training Coordinator',
    'Medical Director',
    'Nursing Head'
  ]);

  const accreditationStatuses = sortStringOptions([
    'NABH Accredited',
    'NABH Inter-Level Accredited',
    'Preparing for Accreditation',
    'Not Accredited',
    'Others',
    'Information Not Available'
  ]);

  // Ordered by sales funnel — do NOT sort alphabetically
  const dispositionStatuses = DISPOSITION_STATUSES;

  const leadSources = sortStringOptions([
    'Demo Request – App Portal',
    'Organic',
    'Referral',
    'Walk-in',
    'Campaign',
    'Social Media',
    'Direct Sign-up'
  ]);

  // Fetch CRM Officers for Super Admin
  useEffect(() => {
    const fetchCrmOfficers = async () => {
      if (userRole === 'Super Admin') {
        setLoadingOfficers(true);
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/crm/officers`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCrmOfficers(response.data || []);
        } catch (error) {
          console.error('Failed to fetch CRM Officers:', error);
          toast.error('Failed to load CRM Officers');
        } finally {
          setLoadingOfficers(false);
        }
      }
    };
    
    fetchCrmOfficers();
  }, [userRole]);

  // In create mode, the current user will be the creator ("Added by")
  useEffect(() => {
    if (!isEditMode) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        setAddedByName(u.full_name || u.name || u.email || '');
      } catch (e) { setAddedByName(''); }
    }
  }, [isEditMode]);

  // Fetch existing lead data if in edit mode
  useEffect(() => {
    const fetchLeadData = async () => {
      if (isEditMode && leadId) {
        setLoadingLead(true);
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/crm/leads/${leadId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const leadData = response.data;
          
          // Check if lead is converted to hospital - lock editing for everyone
          if (leadData.converted_at || leadData.converted_to_hospital_id || leadData.disposition_status === 'Converted') {
            setIsConverted(true);
            setConvertedHospitalId(leadData.converted_to_hospital_id);
          }
          
          // Pre-fill form with lead data
          setFormData({
            institution_type: leadData.institution_type || '',
            hospital_name: leadData.hospital_name || '',
            hospital_address: leadData.hospital_address || '',
            country: leadData.country || 'India',
            state: leadData.state || '',
            city: leadData.city || '',
            pin_code: leadData.pin_code || '',
            contact_email: leadData.contact_email || '',
            contact_phone: leadData.contact_phone || '',
            number_of_beds: leadData.number_of_beds || '',
            number_of_staff: leadData.number_of_staff || '',
            number_of_concurrent_staff: leadData.number_of_concurrent_staff || 0,
            number_of_staff_for_quotation: leadData.number_of_staff_for_quotation || 0,
            gstin_uin: leadData.gstin_uin || '',
            pan_it: leadData.pan_it || '',
            accreditation_status: leadData.accreditation_status || '',
            disposition_status: leadData.disposition_status || '',
            follow_up_action: leadData.follow_up_action || '',
            important_notes: leadData.important_notes || '',
            lead_source: leadData.lead_source || '',
            quoted_amount: leadData.quoted_amount || 0,
            assigned_officer_email: leadData.assigned_officer_email || ''
          });
          
          // Original creator for the header
          setAddedByName(leadData.lead_owner_name || leadData.created_by_name || '');

          // Pre-fill SPOC list
          if (leadData.spoc_details && leadData.spoc_details.length > 0) {
            setSpocList(leadData.spoc_details);
          }

        } catch (error) {
          console.error('Failed to fetch lead data:', error);
          toast.error('Failed to load lead data');
          navigate('/crm');
        } finally {
          setLoadingLead(false);
        }
      }
    };
    
    fetchLeadData();
  }, [isEditMode, leadId]);

  // Click outside handler for institution suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        institutionInputRef.current &&
        !institutionInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced institution name search
  const fetchInstitutionSuggestions = useCallback(
    async (query) => {
      if (query.length < 3) {
        setInstitutionSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      
      setLoadingSuggestions(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/crm/institution-names`, {
          params: { query },
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const suggestions = response.data?.suggestions || [];
        setInstitutionSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
      } catch (error) {
        console.error('Failed to fetch institution suggestions:', error);
        setInstitutionSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    },
    []
  );

  // Real-time duplicate institution check (create mode only)
  const checkDuplicateInstitution = useCallback(async (name) => {
    const normalized = (name || '').trim();
    if (isEditMode || normalized.length < 3) {
      setDuplicateInfo(null);
      return null;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crm/leads/check-duplicate`, {
        params: { name: normalized },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.exists) {
        setDuplicateInfo(response.data);
        return response.data;
      }
      setDuplicateInfo(null);
      return null;
    } catch (error) {
      console.error('Duplicate check failed:', error);
      setDuplicateInfo(null);
      return null;
    }
  }, [isEditMode]);

  // Handle institution name change with debouncing
  const handleInstitutionNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, hospital_name: value }));
    setDuplicateInfo(null);
    setDuplicateDismissed(false);

    // Clear existing timeout
    if (window.institutionSearchTimeout) {
      clearTimeout(window.institutionSearchTimeout);
    }

    // Debounce the search
    window.institutionSearchTimeout = setTimeout(() => {
      fetchInstitutionSuggestions(value);
      checkDuplicateInstitution(value);
    }, 300);
  };

  // Handle selecting a suggestion
  const handleSelectSuggestion = (name) => {
    setFormData(prev => ({ ...prev, hospital_name: name }));
    setShowSuggestions(false);
    setInstitutionSuggestions([]);
    setDuplicateDismissed(false);
    checkDuplicateInstitution(name);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Auto-calculate concurrent staff (10% of staff count)
    if (name === 'number_of_staff') {
      const staffNum = parseInt(value) || 0;
      setFormData(prev => ({
        ...prev,
        [name]: value,
        number_of_concurrent_staff: Math.floor(staffNum * 0.1)
      }));
    }
  };

  const handleSpocChange = (index, field, value) => {
    const updated = [...spocList];
    updated[index][field] = value;
    setSpocList(updated);
  };

  const addSpoc = () => {
    setSpocList([...spocList, {
      spoc_name: '',
      spoc_mobile: '',
      spoc_email: '',
      spoc_designation: '',
      remarks: ''
    }]);
  };

  const removeSpoc = (index) => {
    if (spocList.length > 1) {
      setSpocList(spocList.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.hospital_name) {
      toast.error('Institution name is required');
      return;
    }
    if (!formData.disposition_status) {
      toast.error('Disposition status is required');
      return;
    }
    // Contact details now live in the Primary Contact (SPOC) section — require at least one
    // complete contact with a valid email.
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const cleanSpocs = (spocList || [])
      .map((s) => ({ ...s }))
      .filter((s) => [s.spoc_name, s.spoc_email, s.spoc_mobile, s.spoc_designation].some((v) => (v || '').trim()));
    if (cleanSpocs.length === 0) {
      toast.error('At least one contact person is required');
      return;
    }
    for (const s of cleanSpocs) {
      if (!(s.spoc_name || '').trim()) { toast.error('Contact name is required for each contact'); return; }
      if (!emailRe.test((s.spoc_email || '').trim())) { toast.error('A valid SPOC email is required for each contact'); return; }
    }

    // Duplicate institution guard (create mode only) — re-check right before saving.
    // Surface the result in the popup (not a toast) and block the save.
    if (!isEditMode) {
      const dup = await checkDuplicateInstitution(formData.hospital_name);
      if (dup && dup.exists) {
        setDuplicateInfo(dup);
        setDuplicateDismissed(false);
        return;
      }
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Normalize empty rich-text content (Quill emits <p><br></p> when empty) to ''
      const strippedNotes = (formData.important_notes || '').replace(/<(p|br|span|div)[^>]*>/gi, '').replace(/<\/(p|span|div)>/gi, '').replace(/&nbsp;/gi, '').trim();
      const normalizedNotes = strippedNotes.length === 0 ? '' : formData.important_notes;

      const payload = {
        ...formData,
        important_notes: normalizedNotes,
        spoc_details: cleanSpocs,
        number_of_concurrent_staff: parseInt(formData.number_of_concurrent_staff) || 0,
        number_of_staff_for_quotation: parseInt(formData.number_of_staff_for_quotation) || 0,
        quoted_amount: parseFloat(formData.quoted_amount) || 0
      };

      // Contact Email/Phone removed from the form — don't send empty values (backend
      // backfills from the first SPOC and rejects empty-string emails).
      if (!payload.contact_email) delete payload.contact_email;
      if (!payload.contact_phone) delete payload.contact_phone;

      // Remove assigned_officer_email if it's "unassigned" or empty
      if (payload.assigned_officer_email === 'unassigned' || !payload.assigned_officer_email) {
        delete payload.assigned_officer_email;
      }
      
      if (isEditMode) {
        // Update existing lead
        const response = await axios.put(`${API}/crm/leads/${leadId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Check for conversion response
        if (response.data?.conversion) {
          if (response.data.conversion.status === "success") {
            toast.success('Lead disposition updated successfully! Institution onboarded.');
          } else if (response.data.conversion.status === "exists") {
            toast.info('Lead disposition updated successfully! Institution already exists.');
          }
        } else {
          toast.success('Lead disposition updated successfully!');
        }
      } else {
        // Create new lead
        const response = await axios.post(`${API}/crm/leads`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // If this lead was created from a demo, link them
        if (demoData?.demo_id && response.data?.lead_id) {
          try {
            await axios.put(
              `${API}/demo-requests/${demoData.demo_id}/link-lead?lead_id=${response.data.lead_id}`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (linkError) {
            console.error('Failed to link demo to lead:', linkError);
            // Don't fail the whole operation if linking fails
          }
        }
        
        toast.success('Lead created successfully!');
      }
      
      navigate('/crm');
    } catch (error) {
      toast.error(getApiErrorMessage(error, `Failed to ${isEditMode ? 'update' : 'create'} lead`));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      {/* Loading State */}
      {loadingLead && (
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="w-16 h-16 animate-spin text-[#0056B3]" />
        </div>
      )}
      
      {!loadingLead && (
        <>
          {/* Header */}
          <header className="bg-white border-b border-gray-200 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center py-3 min-h-16">
                <div>
                  <h1 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>
                    {isConverted ? 'View Lead (Converted to Institution)' : (isEditMode ? 'Edit Lead' : 'Add New Lead')}
                  </h1>
                  {addedByName && (
                    <p className="text-sm text-gray-500 mt-0.5">
                      Added by: <span className="font-medium text-gray-700">{addedByName}</span>
                    </p>
                  )}
                </div>
                <Button
                  onClick={() => navigate('/crm')}
                  variant="outline"
                  className="border-[#0056B3] text-[#0056B3]"
                >
                  Back to Leads
                </Button>
              </div>
            </div>
          </header>

          {/* Form */}
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Converted Lead Banner - Read Only */}
            {isConverted && (
              <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Read-Only Mode</h3>
                    <p className="text-sm text-yellow-700 mt-1">
                      This lead has been converted to an institution and cannot be edited. 
                      To make changes, please edit the institution record directly.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className={`bg-white rounded-2xl p-8 shadow-xl ${isConverted ? 'opacity-80' : ''}`}>
          {/* Institution Basic Information */}
          <div className="mb-8">
            <h2 className="text-[18px] font-semibold mb-4" style={{ color: '#0056B3' }}>Institution Basic Information</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label>Type of Institution *</Label>
                <Select value={formData.institution_type} onValueChange={(value) => setFormData({ ...formData, institution_type: value })} disabled={isConverted}>
                  <SelectTrigger><SelectValue placeholder="Select institution type" /></SelectTrigger>
                  <SelectContent>
                    {institutionTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="relative">
                <Label>Institution Name *</Label>
                <div className="relative">
                  <Input 
                    ref={institutionInputRef}
                    name="hospital_name" 
                    value={formData.hospital_name} 
                    onChange={handleInstitutionNameChange} 
                    onFocus={() => {
                      if (institutionSuggestions.length > 0) {
                        setShowSuggestions(true);
                      }
                    }}
                    placeholder="Enter institution name (type 3+ characters for suggestions)" 
                    disabled={isConverted}
                    autoComplete="off"
                  />
                  {loadingSuggestions && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    </div>
                  )}
                </div>
                
                {/* Suggestions Dropdown */}
                {showSuggestions && institutionSuggestions.length > 0 && (
                  <div 
                    ref={suggestionsRef}
                    className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  >
                    <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                      <Building2 className="w-3 h-3" />
                      <span>Existing institutions matching your search:</span>
                    </div>
                    {institutionSuggestions.map((name, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelectSuggestion(name)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors border-b border-gray-50 last:border-b-0 flex items-center gap-2"
                      >
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span>{name}</span>
                      </button>
                    ))}
                    <div className="px-3 py-2 text-xs text-gray-400 bg-gray-50 border-t border-gray-100">
                      Select an existing institution or continue typing to create a new one
                    </div>
                  </div>
                )}

              </div>
              <div className="md:col-span-2">
                <Label>Institution Address *</Label>
                <Textarea name="hospital_address" value={formData.hospital_address} onChange={handleChange} placeholder="Enter complete address" rows={3} disabled={isConverted} />
              </div>
              <div>
                <Label>Country *</Label>
                <Input name="country" value={formData.country} onChange={handleChange} disabled={isConverted} />
              </div>
              <div>
                <Label>State *</Label>
                <Input name="state" value={formData.state} onChange={handleChange} placeholder="Enter state" disabled={isConverted} />
              </div>
              <div>
                <Label>City *</Label>
                <Input name="city" value={formData.city} onChange={handleChange} placeholder="Enter city" disabled={isConverted} />
              </div>
              <div>
                <Label>Pin Code *</Label>
                <Input name="pin_code" value={formData.pin_code} onChange={handleChange} placeholder="Enter pin code" disabled={isConverted} />
              </div>
            </div>
          </div>

          {/* Primary Contact (SPOC) Details — supports multiple contact persons */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-[18px] font-semibold" style={{ color: '#0056B3' }}>Primary Contact Details</h2>
              {!isConverted && (
                <Button type="button" onClick={addSpoc} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Add Another Contact
                </Button>
              )}
            </div>
            {spocList.map((spoc, index) => (
              <div key={index} className="border rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium">Contact {index + 1}</h3>
                  {spocList.length > 1 && !isConverted && (
                    <Button type="button" onClick={() => removeSpoc(index)} variant="ghost" size="sm">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>SPOC Name *</Label>
                    <Input value={spoc.spoc_name} onChange={(e) => handleSpocChange(index, 'spoc_name', e.target.value)} placeholder="Enter name" disabled={isConverted} />
                  </div>
                  <div>
                    <Label>SPOC Mobile *</Label>
                    <Input value={spoc.spoc_mobile} onChange={(e) => handleSpocChange(index, 'spoc_mobile', e.target.value)} placeholder="Enter mobile" disabled={isConverted} />
                  </div>
                  <div>
                    <Label>SPOC Email *</Label>
                    <Input type="email" value={spoc.spoc_email} onChange={(e) => handleSpocChange(index, 'spoc_email', e.target.value)} placeholder="Enter email" disabled={isConverted} />
                  </div>
                  <div>
                    <Label>SPOC Designation *</Label>
                    <Select value={spoc.spoc_designation} onValueChange={(value) => handleSpocChange(index, 'spoc_designation', value)} disabled={isConverted}>
                      <SelectTrigger><SelectValue placeholder="Select designation" /></SelectTrigger>
                      <SelectContent>
                        {spocDesignations.map(des => (
                          <SelectItem key={des} value={des}>{des}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Remarks</Label>
                    <Textarea value={spoc.remarks} onChange={(e) => handleSpocChange(index, 'remarks', e.target.value)} placeholder="Additional notes" rows={2} disabled={isConverted} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Institution Details */}
          <div className="mb-8">
            <h2 className="text-[18px] font-semibold mb-4" style={{ color: '#0056B3' }}>Institution Details</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label>Number of Beds *</Label>
                <Select value={formData.number_of_beds} onValueChange={(value) => setFormData({ ...formData, number_of_beds: value })} disabled={isConverted}>
                  <SelectTrigger><SelectValue placeholder="Select number of beds" /></SelectTrigger>
                  <SelectContent>
                    {bedOptions.map(option => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Number of Staff *</Label>
                <Select value={formData.number_of_staff} onValueChange={(value) => handleChange({ target: { name: 'number_of_staff', value } })} disabled={isConverted}>
                  <SelectTrigger><SelectValue placeholder="Select number of staff" /></SelectTrigger>
                  <SelectContent>
                    {staffOptions.map(option => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Number of Concurrent Staff</Label>
                <Input name="number_of_concurrent_staff" type="number" value={formData.number_of_concurrent_staff} onChange={handleChange} placeholder="Auto-calculated" disabled={isConverted} />
              </div>
              <div>
                <Label>Number of Staff for Quotation</Label>
                <Input name="number_of_staff_for_quotation" type="number" value={formData.number_of_staff_for_quotation} onChange={handleChange} disabled={isConverted} />
              </div>
              <div>
                <Label>GSTIN/UIN Number</Label>
                <Input name="gstin_uin" value={formData.gstin_uin} onChange={handleChange} disabled={isConverted} />
              </div>
              <div>
                <Label>PAN/IT Number</Label>
                <Input name="pan_it" value={formData.pan_it} onChange={handleChange} disabled={isConverted} />
              </div>
              <div className="md:col-span-2">
                <Label>Accreditation Status *</Label>
                <Select value={formData.accreditation_status} onValueChange={(value) => setFormData({ ...formData, accreditation_status: value })} disabled={isConverted}>
                  <SelectTrigger><SelectValue placeholder="Select accreditation status" /></SelectTrigger>
                  <SelectContent>
                    {accreditationStatuses.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Lead Disposition */}
          <div className="mb-8">
            <h2 className="text-[18px] font-semibold mb-4" style={{ color: '#0056B3' }}>Lead Disposition</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label>Disposition Status *</Label>
                <Select 
                  value={formData.disposition_status}
                  onValueChange={(value) => setFormData({ ...formData, disposition_status: value })}
                  disabled={isConverted}
                >
                  <SelectTrigger><SelectValue placeholder="Select disposition status" /></SelectTrigger>
                  <SelectContent>
                    {dispositionStatuses.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Lead Source *</Label>
                <Select 
                  value={formData.lead_source}
                  onValueChange={(value) => setFormData({ ...formData, lead_source: value })}
                  disabled={isConverted}
                >
                  <SelectTrigger><SelectValue placeholder="Select lead source" /></SelectTrigger>
                  <SelectContent>
                    {leadSources.map(source => (
                      <SelectItem key={source} value={source}>{source}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quoted Amount (₹)</Label>
                <Input name="quoted_amount" type="number" value={formData.quoted_amount} onChange={handleChange} placeholder="0" disabled={isConverted} />
              </div>
              {userRole === 'Super Admin' && !isConverted && (
                <div>
                  <Label>Assign to CRM Officer</Label>
                  <Select 
                    value={formData.assigned_officer_email}
                    onValueChange={(value) => setFormData({ ...formData, assigned_officer_email: value })}
                    disabled={loadingOfficers}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingOfficers ? "Loading officers..." : "Select CRM Officer (Optional)"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {crmOfficers.map(officer => (
                        <SelectItem key={officer.email} value={officer.email}>
                          {officer.full_name} ({officer.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="md:col-span-2">
                <Label>Follow-up Action</Label>
                <Textarea name="follow_up_action" value={formData.follow_up_action} onChange={handleChange} placeholder="Next steps" rows={3} disabled={isConverted} />
              </div>
              <div className="md:col-span-2">
                <Label>Important Notes</Label>
                <div className="bg-white rounded-md [&_.ql-editor]:min-h-[90px] [&_.ql-editor]:max-h-[180px] [&_.ql-editor]:overflow-y-auto">
                  <ReactQuill
                    theme="snow"
                    value={formData.important_notes || ''}
                    onChange={(value) => setFormData(prev => ({ ...prev, important_notes: value }))}
                    modules={notesModules}
                    formats={notesFormats}
                    readOnly={isConverted}
                    placeholder="Additional information"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <Button type="button" variant="outline" onClick={() => navigate('/crm')}>
              {isConverted ? 'Back to Leads' : 'Cancel'}
            </Button>
            {!isConverted && (
              <Button type="submit" disabled={loading} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEditMode ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  isEditMode ? 'Update Lead' : 'Save Lead'
                )}
              </Button>
            )}
          </div>
        </form>
      </main>
      </>
      )}

      {/* Duplicate institution popup */}
      <Dialog
        open={!isEditMode && !!duplicateInfo?.exists && !duplicateDismissed}
        onOpenChange={(open) => { if (!open) setDuplicateDismissed(true); }}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Institution already exists</DialogTitle>
          </DialogHeader>
          {duplicateInfo?.is_own ? (
            <>
              <p className="text-sm text-gray-700">
                This institution already exists in your leads. Would you like to edit the existing lead?
              </p>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setDuplicateDismissed(true)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
                  onClick={() => {
                    const existingId = duplicateInfo?.lead_id;
                    setDuplicateDismissed(true);
                    setDuplicateInfo(null);
                    if (existingId) navigate(`/crm/leads/${existingId}/edit`);
                  }}
                >
                  Edit Existing
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-700">
                This institution has already been added by <span className="font-medium">{duplicateInfo?.owner_name}</span>. Please contact the owner or your administrator if any updates are required.
              </p>
              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
                  onClick={() => setDuplicateDismissed(true)}
                >
                  OK
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddLeadPage;